import { z } from 'zod';

/**
 * Contrast Advisor — WCAG colour-contrast computation library +
 * wire shapes (`pending-contrast-advisor-a11y`).
 *
 * This module lives in `@rynora/contracts` for one simple reason:
 * the pure colour math is needed in three places (the runner, the
 * API, and the frontend preview) and contracts is the only
 * package all three can import without circular dependencies.
 *
 * The math is intentionally hand-rolled (no `culori` / `colord` /
 * etc. runtime dep) for three reasons:
 *   • The WCAG 2.1 formula is small (~50 LOC) and stable.
 *   • The Oklch lightness sweep we use for "suggested-fix" is
 *     short enough to inline and is the one place where we want
 *     full control over the perceptual ramp.
 *   • `@rynora/contracts` keeps a zero-runtime-dep posture
 *     (only `zod`).
 *
 * The reference is WCAG 2.1 Technique G18 — relative luminance:
 *   https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 *
 * v1 scope:
 *   • Pure math (no DOM, no Playwright, no Sharp).
 *   • Threshold table + AA/AAA toggle.
 *   • Suggested-fix sweep using Oklch lightness adjustment.
 *
 * v2 follow-ups (NOT here):
 *   • Cross-state contrast (hover / focus / active) — needs runner DOM injection.
 *   • Brand-palette constraint — suggested-fix snapped to a registered palette.
 *   • AI-rewrite via intent-engine (`getEffectiveAiCreds('a11y-fix')`).
 */

// =====================================================================
// WCAG thresholds. The table mirrors WCAG 2.1 SC 1.4.3 / 1.4.6.
// "Large text" = >= 18pt or >= 14pt + bold. The runner classifies
// per-element and stores `is_large_text` on the violation row.
// =====================================================================

export const WCAG_LEVELS = ['AA', 'AAA'] as const;
export type WcagLevel = (typeof WCAG_LEVELS)[number];
export const WcagLevelSchema = z.enum(WCAG_LEVELS);

export interface WcagThreshold {
  /** Ratio required for non-large body text. */
  normal: number;
  /** Ratio required for "large" text (>= 18pt or >= 14pt bold). */
  large: number;
}

export const CONTRAST_THRESHOLDS: Record<WcagLevel, WcagThreshold> = {
  AA: { normal: 4.5, large: 3 },
  AAA: { normal: 7, large: 4.5 },
};

/** Look up the threshold for a level + text-size class. */
export function getContrastThreshold(level: WcagLevel, isLargeText: boolean): number {
  const row = CONTRAST_THRESHOLDS[level];
  return isLargeText ? row.large : row.normal;
}

// =====================================================================
// Hex parsing + sRGB linearisation.
// =====================================================================

const HEX_3_OR_6 = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Components are in the 0..1 sRGB range (NOT 0..255). */
export interface RgbColour {
  r: number;
  g: number;
  b: number;
}

/**
 * Parse `#rgb` / `#rrggbb` into a normalised 0..1 sRGB triple.
 * Throws on invalid input — the caller is expected to validate
 * via `HexColourSchema` if it can't be sure.
 */
export function hexToRgb(hex: string): RgbColour {
  if (!HEX_3_OR_6.test(hex)) {
    throw new Error(`Invalid hex colour '${hex}'. Expected #rgb or #rrggbb.`);
  }
  const v = hex.startsWith('#') ? hex.slice(1) : hex;
  const expanded = v.length === 3 ? v.split('').map((c) => c + c).join('') : v;
  return {
    r: parseInt(expanded.substring(0, 2), 16) / 255,
    g: parseInt(expanded.substring(2, 4), 16) / 255,
    b: parseInt(expanded.substring(4, 6), 16) / 255,
  };
}

export const HexColourSchema = z.string().regex(HEX_3_OR_6, 'Expected #rgb or #rrggbb hex colour.');

/** Inverse of `hexToRgb`. Clamps + rounds to two-hex-digits per channel. */
export function rgbToHex(rgb: RgbColour): string {
  const channel = (c: number) =>
    Math.max(0, Math.min(255, Math.round(c * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(rgb.r)}${channel(rgb.g)}${channel(rgb.b)}`;
}

/**
 * sRGB → linear-light conversion (WCAG 2.1 G18). Required for the
 * luminance computation; not the same as a perceptual transform.
 */
function srgbToLinear(c: number): number {
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** WCAG 2.1 G18 relative luminance. Output in [0, 1]. */
export function relativeLuminance({ r, g, b }: RgbColour): number {
  const R = srgbToLinear(r);
  const G = srgbToLinear(g);
  const B = srgbToLinear(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/**
 * WCAG 2.1 contrast ratio. Output in [1, 21]. We bias to the
 * larger luminance in the numerator so callers don't have to
 * worry about argument order.
 */
export function computeContrastRatio(fg: RgbColour | string, bg: RgbColour | string): number {
  const fgRgb = typeof fg === 'string' ? hexToRgb(fg) : fg;
  const bgRgb = typeof bg === 'string' ? hexToRgb(bg) : bg;
  const L1 = relativeLuminance(fgRgb);
  const L2 = relativeLuminance(bgRgb);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
}

// =====================================================================
// Oklch lightness sweep for the suggested-fix colour. Oklch is
// the colour space behind the new CSS `oklch()` notation — its
// L axis is perceptually uniform, so a 0.05 step in `L` looks
// like a 0.05 step in every other point on the wheel. That's
// exactly what we want for "darken the foreground until contrast
// crosses the threshold while keeping the hue and saturation".
//
// Reference: https://bottosson.github.io/posts/oklab/
// =====================================================================

interface Oklab {
  L: number;
  a: number;
  b: number;
}

interface Oklch {
  L: number;
  C: number;
  h: number;
}

/** sRGB (0..1) → linear (0..1), no gamma. */
function gammaToLinear(c: number): number {
  return srgbToLinear(c);
}

/** linear (0..1) → sRGB (0..1) with WCAG gamma curve. */
function linearToGamma(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

/** sRGB → Oklab (per Björn Ottosson's reference impl). */
function rgbToOklab({ r, g, b }: RgbColour): Oklab {
  const lr = gammaToLinear(r);
  const lg = gammaToLinear(g);
  const lb = gammaToLinear(b);

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return {
    L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  };
}

/** Oklab → sRGB (with gentle clamping; the sweep may briefly land
 * outside the sRGB gamut and we just clip back). */
function oklabToRgb({ L, a, b }: Oklab): RgbColour {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  const lr = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const lg = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const lb = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

  return {
    r: clamp01(linearToGamma(lr)),
    g: clamp01(linearToGamma(lg)),
    b: clamp01(linearToGamma(lb)),
  };
}

function clamp01(c: number): number {
  return Math.max(0, Math.min(1, c));
}

/** Convert Oklab to Oklch (cylindrical form — hue + chroma). */
function oklabToOklch({ L, a, b }: Oklab): Oklch {
  const C = Math.sqrt(a * a + b * b);
  const h = Math.atan2(b, a); // radians
  return { L, C, h };
}

function oklchToOklab({ L, C, h }: Oklch): Oklab {
  return { L, a: C * Math.cos(h), b: C * Math.sin(h) };
}

/** sRGB → Oklch (composition; exported for tests). */
export function rgbToOklch(rgb: RgbColour): Oklch {
  return oklabToOklch(rgbToOklab(rgb));
}

/** Oklch → sRGB (composition; exported for tests). */
export function oklchToRgb(c: Oklch): RgbColour {
  return oklabToRgb(oklchToOklab(c));
}

// =====================================================================
// Suggested-fix.
//
// Algorithm: hold the foreground's hue + chroma, sweep its
// lightness in fine steps toward whichever extreme (0 or 1) is
// further from the background's lightness, until the WCAG ratio
// meets the threshold. If the sweep would have to leave the
// 0..1 lightness range to satisfy the threshold (extreme
// background or extreme threshold), return null — the UI should
// then fall back to "no clean fix; pick a new colour from
// scratch".
// =====================================================================

export interface SuggestFixOptions {
  /** WCAG threshold to satisfy (e.g. `4.5` for AA normal text). */
  targetRatio: number;
  /**
   * Number of sweep steps. The 100-step default is well below
   * any human-perceivable difference and finishes in a few
   * microseconds.
   */
  steps?: number;
}

export interface SuggestFixResult {
  foregroundHex: string;
  achievedRatio: number;
}

/**
 * Return the nearest WCAG-compliant foreground for the given
 * background. `null` if no in-gamut colour on the same hue +
 * chroma meets the threshold.
 *
 * We sweep darker if the background is lighter, and lighter if
 * the background is darker — choosing the direction that
 * generally increases contrast.
 */
export function suggestFixColor(
  fg: RgbColour | string,
  bg: RgbColour | string,
  options: SuggestFixOptions,
): SuggestFixResult | null {
  const fgRgb = typeof fg === 'string' ? hexToRgb(fg) : fg;
  const bgRgb = typeof bg === 'string' ? hexToRgb(bg) : bg;
  const fgOklch = rgbToOklch(fgRgb);

  const bgLum = relativeLuminance(bgRgb);
  const fgLum = relativeLuminance(fgRgb);
  // Direction: if the bg is darker than the fg, we sweep the
  // fg even lighter (toward L=1); if the bg is lighter, we
  // darken the fg (toward L=0).
  const sweepDirection = bgLum < fgLum ? +1 : -1;

  const steps = options.steps ?? 100;
  // Walk in 0..1 lightness steps; the loop emits the first
  // colour to satisfy the ratio, which is also the closest one
  // perceptually to the original (per Oklch's perceptual axis).
  for (let i = 1; i <= steps; i++) {
    const delta = (sweepDirection * i) / steps;
    const candidateL = clamp01(fgOklch.L + delta);
    const candidateRgb = oklchToRgb({ L: candidateL, C: fgOklch.C, h: fgOklch.h });
    const ratio = computeContrastRatio(candidateRgb, bgRgb);
    if (ratio >= options.targetRatio) {
      return { foregroundHex: rgbToHex(candidateRgb), achievedRatio: ratio };
    }
    // Bail early if we hit the gamut wall — sweeping further
    // can only clip back to the same colour we already tried.
    if (candidateL === 0 || candidateL === 1) {
      break;
    }
  }
  return null;
}

// =====================================================================
// One-shot element assessor. Wraps `computeContrastRatio` +
// `suggestFixColor` into the shape the runner emits per element.
// =====================================================================

export interface ContrastAssessmentInput {
  foregroundHex: string;
  backgroundHex: string;
  /** Whether the text is "large" (>= 18pt or >= 14pt bold). */
  isLargeText: boolean;
  wcagLevel: WcagLevel;
}

export interface ContrastAssessmentResult {
  ratio: number;
  threshold: number;
  passes: boolean;
  /**
   * Only populated when `passes === false`. `null` means the
   * fix algorithm couldn't find an in-gamut colour on the same
   * hue+chroma — the UI shows a "pick a new colour" hint.
   */
  suggestedForegroundHex: string | null;
  suggestedRatio: number | null;
}

export function assessContrast(input: ContrastAssessmentInput): ContrastAssessmentResult {
  const ratio = computeContrastRatio(input.foregroundHex, input.backgroundHex);
  const threshold = getContrastThreshold(input.wcagLevel, input.isLargeText);
  const passes = ratio >= threshold;
  if (passes) {
    return { ratio, threshold, passes, suggestedForegroundHex: null, suggestedRatio: null };
  }
  const fix = suggestFixColor(input.foregroundHex, input.backgroundHex, { targetRatio: threshold });
  return {
    ratio,
    threshold,
    passes,
    suggestedForegroundHex: fix?.foregroundHex ?? null,
    suggestedRatio: fix?.achievedRatio ?? null,
  };
}

// =====================================================================
// Wire schemas — the API + UI both bind to these.
// =====================================================================

/**
 * Phase B — cross-state checks. The runner can re-run a contrast
 * pass while CSS pseudo-states are active by injecting style
 * overrides into the page. `resting` is the v1 baseline; the
 * other three exercise hover / focus / active behaviour that a
 * traditional contrast scan misses.
 */
export const CONTRAST_STATES = ['resting', 'hover', 'focus', 'active'] as const;
export type ContrastState = (typeof CONTRAST_STATES)[number];
export const ContrastStateSchema = z.enum(CONTRAST_STATES);

export const ContrastBoundingBoxSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
});
export type ContrastBoundingBox = z.infer<typeof ContrastBoundingBoxSchema>;

export const ContrastViolationSchema = z.object({
  id: z.string().uuid(),
  test_run_id: z.string().uuid(),
  /**
   * Optional pointer to the specific step within the run. The
   * v1 schema stores the step's position in the canvas (0-indexed)
   * because we don't have a per-step row to FK against.
   */
  step_index: z.number().int().nonnegative().nullable(),
  selector: z.string().min(1),
  foreground_hex: HexColourSchema,
  background_hex: HexColourSchema,
  computed_ratio: z.number().min(1).max(21),
  wcag_threshold: z.number().min(1).max(21),
  wcag_level: WcagLevelSchema,
  is_large_text: z.boolean(),
  suggested_foreground_hex: HexColourSchema.nullable(),
  proof_image_url: z.string().url().nullable(),
  bounding_box: ContrastBoundingBoxSchema,
  /**
   * Phase B — which CSS pseudo-state was active when the
   * violation was sampled. `resting` covers the standard case
   * (no pseudo-state); the other three are the cross-state
   * extensions. The DB default is `resting` so legacy rows from
   * the v1 migration are forward-compatible.
   */
  state: ContrastStateSchema,
  /**
   * Phase C — when a brand palette is configured, the suggested
   * fix snaps to the nearest WCAG-compliant entry instead of
   * the arbitrary Oklch sweep result. NULL = no palette match
   * available (sweep result returned) OR no palette configured.
   */
  palette_snap_id: z.string().uuid().nullable(),
  created_at: z.string(),
});
export type ContrastViolation = z.infer<typeof ContrastViolationSchema>;

export const ListContrastViolationsResponseSchema = z.object({
  violations: z.array(ContrastViolationSchema),
  /** Aggregate count per WCAG level for the summary bar. */
  summary: z.object({
    total: z.number().int().nonnegative(),
    aa_failing: z.number().int().nonnegative(),
    aaa_failing: z.number().int().nonnegative(),
  }),
});
export type ListContrastViolationsResponse = z.infer<typeof ListContrastViolationsResponseSchema>;

/**
 * Audit-only POST — the FE fires this when the user clicks the
 * "Copy CSS patch" button so we can compute adoption rate
 * later. No state change. The response is `204 No Content`.
 */
export const LogFixCopyRequestSchema = z.object({
  violation_id: z.string().uuid(),
  /** Hex the user actually copied (might differ from the
   * suggested colour if the brand-palette constraint kicks in). */
  copied_foreground_hex: HexColourSchema,
});
export type LogFixCopyRequest = z.infer<typeof LogFixCopyRequestSchema>;

// =====================================================================
// Phase A — Worker-write batch insert. The runner (and any
// external scanner) POSTs a batch of violations after a run
// finishes. The route resolves the proof image (Sharp crop)
// before responding so the FE never sees `proof_image_url=null`
// once the worker pipeline is online.
// =====================================================================

export const ContrastViolationDraftSchema = z.object({
  step_index: z.number().int().nonnegative().nullable().optional(),
  selector: z.string().min(1).max(2048),
  foreground_hex: HexColourSchema,
  background_hex: HexColourSchema,
  is_large_text: z.boolean().default(false),
  wcag_level: WcagLevelSchema.default('AA'),
  state: ContrastStateSchema.default('resting'),
  bounding_box: ContrastBoundingBoxSchema,
  /**
   * Optional path within the run's artifact store pointing at
   * the full-page screenshot the API server will crop into a
   * proof image. NULL = no proof image will be generated for
   * this violation.
   */
  source_screenshot_path: z.string().min(1).max(1024).nullable().optional(),
});
export type ContrastViolationDraft = z.infer<typeof ContrastViolationDraftSchema>;

export const InsertContrastViolationsRequestSchema = z.object({
  test_run_id: z.string().uuid(),
  violations: z.array(ContrastViolationDraftSchema).max(500),
});
export type InsertContrastViolationsRequest = z.infer<typeof InsertContrastViolationsRequestSchema>;

// =====================================================================
// Phase C — Brand palettes. An operator can register one or more
// brand palettes; suggested-fix colours snap to the nearest
// in-palette WCAG-compliant entry. Mirrors `palettes` +
// `palette_colours` from migration 087.
// =====================================================================

export const PaletteColourSchema = z.object({
  id: z.string().uuid(),
  palette_id: z.string().uuid(),
  name: z.string().min(1).max(64),
  hex: HexColourSchema,
  display_order: z.number().int().nonnegative(),
});
export type PaletteColour = z.infer<typeof PaletteColourSchema>;

export const PaletteSchema = z.object({
  id: z.string().uuid(),
  org_id: z.string().uuid(),
  name: z.string().min(1).max(64),
  description: z.string().max(240).nullable(),
  is_default: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  colours: z.array(PaletteColourSchema).default([]),
});
export type Palette = z.infer<typeof PaletteSchema>;

export const CreatePaletteRequestSchema = z.object({
  name: z.string().min(1).max(64),
  description: z.string().max(240).nullable().optional(),
  is_default: z.boolean().optional(),
  colours: z
    .array(
      z.object({
        name: z.string().min(1).max(64),
        hex: HexColourSchema,
        display_order: z.number().int().nonnegative().optional(),
      }),
    )
    .max(100)
    .optional(),
});
export type CreatePaletteRequest = z.infer<typeof CreatePaletteRequestSchema>;

export const UpdatePaletteRequestSchema = z.object({
  name: z.string().min(1).max(64).optional(),
  description: z.string().max(240).nullable().optional(),
  is_default: z.boolean().optional(),
  colours: z
    .array(
      z.object({
        name: z.string().min(1).max(64),
        hex: HexColourSchema,
        display_order: z.number().int().nonnegative().optional(),
      }),
    )
    .max(100)
    .optional(),
});
export type UpdatePaletteRequest = z.infer<typeof UpdatePaletteRequestSchema>;

export const ListPalettesResponseSchema = z.object({
  palettes: z.array(PaletteSchema),
});
export type ListPalettesResponse = z.infer<typeof ListPalettesResponseSchema>;

/**
 * Palette-aware suggested-fix variant. Returns the nearest
 * palette entry that meets the target contrast ratio against
 * the background, or null if no palette entry qualifies.
 *
 * "Nearest" is Oklab Δ distance (the perceptual proximity
 * metric); we walk the palette once, keep all WCAG-compliant
 * candidates, and return the one closest to the original
 * foreground colour.
 */
export interface PaletteSnapInput {
  originalForegroundHex: string;
  backgroundHex: string;
  targetRatio: number;
  palette: Pick<PaletteColour, 'hex' | 'id' | 'name'>[];
}

export interface PaletteSnapResult {
  paletteEntryId: string;
  paletteEntryName: string;
  foregroundHex: string;
  achievedRatio: number;
  /** Oklab distance from the original foreground (0 = identical). */
  perceptualDistance: number;
}

export function snapToPalette(input: PaletteSnapInput): PaletteSnapResult | null {
  const origRgb = hexToRgb(input.originalForegroundHex);
  const origOklab = rgbToOklab(origRgb);
  let best: PaletteSnapResult | null = null;
  for (const entry of input.palette) {
    const ratio = computeContrastRatio(entry.hex, input.backgroundHex);
    if (ratio < input.targetRatio) continue;
    const entryOklab = rgbToOklab(hexToRgb(entry.hex));
    const dl = origOklab.L - entryOklab.L;
    const da = origOklab.a - entryOklab.a;
    const db = origOklab.b - entryOklab.b;
    const dist = Math.sqrt(dl * dl + da * da + db * db);
    if (!best || dist < best.perceptualDistance) {
      best = {
        paletteEntryId: entry.id,
        paletteEntryName: entry.name,
        foregroundHex: entry.hex,
        achievedRatio: ratio,
        perceptualDistance: dist,
      };
    }
  }
  return best;
}

// Note: `rgbToOklab` is used by both the Oklch sweep above and
// the palette-snap distance below. TS function declarations
// hoist within a module so `snapToPalette` (defined above) can
// reference the same `rgbToOklab` that the sweep helpers use.

// =====================================================================
// Phase D — AI rewrite. The intent-engine emits a CSS patch the
// dev can copy/paste; rides on `getEffectiveAiCreds('a11yFix')`.
// =====================================================================

export const A11yFixRequestSchema = z.object({
  violation_id: z.string().uuid(),
  /** Free-form context about the surrounding CSS (e.g.
   *  variable names, parent selectors). Optional — the model
   *  can produce a useful patch from the violation row alone. */
  context_css: z.string().max(4096).optional(),
});
export type A11yFixRequest = z.infer<typeof A11yFixRequestSchema>;

export const A11yFixResponseSchema = z.object({
  violation_id: z.string().uuid(),
  /** Replacement-ready CSS snippet. The model is prompted to
   *  return a fenced code block; the API strips the fence. */
  patch: z.string().min(1).max(2048),
  /** Human-readable explanation of why the patch addresses
   *  the WCAG violation. */
  explanation: z.string().max(1024),
  /** Provider+model that produced the patch (for audit). */
  provider: z.string().max(64),
  model: z.string().max(128),
});
export type A11yFixResponse = z.infer<typeof A11yFixResponseSchema>;
